'use client';

import AuditInfoLine from './AuditInfoLine';
import CopyableValue from './CopyableValue';
import EntityHistoryPanel from './EntityHistoryPanel';

export type EntityAuditExtraId = {
  label: string;
  value: string | number | null | undefined;
};

type Props = {
  /** Primary entity ID shown in the tab (copyable). */
  id: string | number | null | undefined;
  /** Optional label for the primary ID row (default: "ID"). */
  idLabel?: string;
  /** Additional identifiers shown below the primary one (e.g. external + DB id). */
  extraIds?: EntityAuditExtraId[];
  /** entityType for EntityHistoryPanel — omit when the entity isn't audited. */
  entityType?: string;
  /** Numeric entity id for EntityHistoryPanel (defaults to numeric `id`). */
  historyEntityId?: number | null;
  createdAt?: string | null;
  modifiedAt?: string | null;
  createdByName?: string | null;
  createdById?: number | null;
  modifiedByName?: string | null;
  modifiedById?: number | null;
  className?: string;
};

function toNumericId(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  return /^\d+$/.test(value) ? Number(value) : null;
}

/**
 * Shared "Systém"/audit tab content for entity detail pages: entity ID
 * (copyable), created/modified info and full change history
 * (EntityHistoryPanel → /api/user/entity-audit/{entityType}/{entityId}).
 */
export default function EntityAuditTab({
  id,
  idLabel,
  extraIds,
  entityType,
  historyEntityId,
  createdAt,
  modifiedAt,
  createdByName,
  createdById,
  modifiedByName,
  modifiedById,
  className,
}: Props) {
  const numericId = historyEntityId ?? toNumericId(id);
  const hasIds = id != null || (extraIds ?? []).some(e => e.value != null);

  return (
    <div className={`space-y-3 ${className ?? ''}`}>
      {hasIds && (
        <div className="border border-wheat/40 rounded-lg p-3 space-y-1.5">
          {id != null && (
            <div className="space-y-0.5">
              <span className="text-sm font-medium text-wood block">{idLabel ?? 'ID'}</span>
              <CopyableValue value={id} className="font-mono text-sm text-bark" />
            </div>
          )}
          {(extraIds ?? []).filter(e => e.value != null).map(e => (
            <div className="space-y-0.5" key={e.label}>
              <span className="text-sm font-medium text-wood block">{e.label}</span>
              <CopyableValue value={e.value} className="font-mono text-sm text-bark" />
            </div>
          ))}
        </div>
      )}
      <AuditInfoLine
        createdAt={createdAt}
        modifiedAt={modifiedAt}
        createdByName={createdByName}
        createdById={createdById}
        modifiedByName={modifiedByName}
        modifiedById={modifiedById}
      />
      {entityType && numericId != null && (
        <EntityHistoryPanel entityType={entityType} entityId={numericId} />
      )}
    </div>
  );
}
